import { getAuthToken, handleApiError } from './apiUtils';
import { Rider, CreateRiderData, UpdateRiderData, RiderResponse, City } from '../store/slices/riderSlice';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1';

export interface RiderListParams {
    page?: number;
    per_page?: number;
    query?: string;
    city_id?: number;
    status?: string;
}

export interface BackendResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export const riderApi = {
    // Get riders list with filtering and pagination
    getRiders: async (params?: RiderListParams): Promise<RiderResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/riders?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<RiderResponse> = await response.json();
        return data.data;
    },

    // Get single rider details
    getRider: async (id: number): Promise<{ rider: Rider }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ rider: Rider }> = await response.json();
        return data.data;
    },

    // Create new rider
    createRider: async (riderData: CreateRiderData): Promise<{ rider: Rider }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(riderData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ rider: Rider }> = await response.json();
        return data.data;
    },

    // Update rider
    updateRider: async (id: number, riderData: UpdateRiderData): Promise<{ rider: Rider }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(riderData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ rider: Rider }> = await response.json();
        return data.data;
    },

    // Delete rider
    deleteRider: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle rider status (activate/deactivate)
    toggleRiderStatus: async (id: number): Promise<{ rider: Rider }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ rider: Rider }> = await response.json();
        return data.data;
    },

    // Get cities for dropdown
    getCities: async (): Promise<City[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/riders/cities`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<City[]> = await response.json();
        return data.data;
    },
};
